import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { build, buildParams, pool } from 'src/app/common/classes/request-builder';
import { BaseForm } from 'src/app/view/base-form';

import { DashboardService } from 'src/app/service/events/dashboard.service';
import { StockApiService } from 'src/app/service/api/stock-api.service';
import { FormCommonApiService } from 'src/app/service/api/formcommon-api.service';
import { FincasApiService } from 'src/app/service/api/fincas-api.service';
import { ProductosApiService } from 'src/app/service/api/productos-api';
import { VentasApiService } from 'src/app/service/api/ventas-api.service';
import { ClientesApiService } from 'src/app/service/api/clientes-api.service';
import { SiexApiService } from 'src/app/service/api/siex-api.service';

import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { StockageModel } from 'src/app/models/stock/stokage.model';
import { Compradores } from 'src/app/models/ventas/compradores.model';

import { EntradasFormDefinition } from './form-control/form-definition';
import { FormArray, Validators } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
    FormType,
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
    DropdownData,
} from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveFormPayloadSerializer } from '@fitogest/reactive-form-builder/utils';

@Component({
    selector: 'app-entradas-form',
    standalone: false,
    templateUrl: './entradas-form.component.html',
    styleUrls: ['./entradas-form.component.scss']
})
export class EntradasFormComponent extends BaseForm<StockageModel> implements OnInit {
    public form!: EntradasFormDefinition;

    public loadingData = true;

    public constructor(
        public stockApi: StockApiService,
        public formApi: FormCommonApiService,
        public fincasApi: FincasApiService,
        public productosApi: ProductosApiService,
        public ventasApi: VentasApiService,
        public clientesApi: ClientesApiService,
        public siexApi: SiexApiService,
        public override route: ActivatedRoute,
        public override router: Router,
        public dashboardEvents: DashboardService,
        private http: HttpClient
    ) {
        super(
            route,
            router,
            dashboardEvents,
            stockApi.entradas.PUT,
            stockApi.entradas.POST,
            'stock-entradas',
            'Editar Entrada',
            'Crear Entrada',
            'Duplicar Entrada'
        );
    }

    public ngOnInit(): void {
        this.resetExternalDropdowns();

        this.form = new EntradasFormDefinition(
            this.getType(),
            this.getModel() as StockageModel,
            null
        );

        this.form.model = {
            ...this.form.model,
            ...(this.getModel() as StockageModel)
        };

        this.initFormData();
        this.softInit(this.getType());
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<StockageModel>): void {
        this.handleReactiveFormChange(event.value, event.changedField, event.origin);
    }

    public formChanges(formModel: ReactiveFormValue<StockageModel>, lastChangedFieldName: ReactiveFormChangedField<StockageModel>): void {
        this.handleReactiveFormChange(formModel, lastChangedFieldName, 'user');
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<StockageModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged' && event.type !== 'dropdownRefresh') {
            return;
        }

        this.form.model = this.withRepeatableLegacyValues(event.value);
        this.form.tag = event.changedField;
        this.form.refreshProductosList();
        this.form.refreshLineFieldLabels();
    }

    private handleReactiveFormChange(
        formModel: ReactiveFormValue<StockageModel>,
        lastChangedFieldName: ReactiveFormChangedField<StockageModel>,
        origin: string,
    ): void {
        this.form.model = this.withRepeatableLegacyValues(formModel);
        this.form.tag = lastChangedFieldName;
        if (lastChangedFieldName === 'id_comercio') {
            this.syncProveedorFromComercio();
        }

        if (lastChangedFieldName === 'tipo') {
            const isMov = String(this.form.model.tipo ?? '') === 'MOV';
            this.form.updateFincaLabel(isMov);
            this.form.updateFincaRequired(!isMov);

            const control = this.formBuilderRef?.model?.get('id_finca');
            if (control) {
                if (isMov) {
                    control.clearValidators();
                } else {
                    control.setValidators(Validators.required);
                }
                control.updateValueAndValidity();
            }
        }

        if (lastChangedFieldName === 'tipo_dato') {
            if (origin === 'user') {
                this.clearLinePayload();
            }

            this.form.refreshProductosList();
            this.form.refreshLineFieldLabels();
            this.refreshReactiveDropdownMappings();
        }

        // Cuando cambia la parcela destino en un MOV, sincronizar id_finca automáticamente
        if (lastChangedFieldName === 'ubicacion_destino' && String(this.form.model.tipo ?? '') === 'MOV') {
            this.syncFincaFromUbicacionDestino();
        }

        if (lastChangedFieldName === 'id_parcela' && this.form.env.appName === 'covidai') {
            this.syncSelectedParcel();
        }
    }

    public onAfterSend(_response: any): void {
        const extractId = (val: unknown): string => {
            if (val == null) return '';
            if (typeof val === 'object') return String((val as Record<string, unknown>)['id'] ?? '');
            return String(val);
        };

        if (String(this.form.model.tipo ?? '') === 'MOV') {
            const extractIdFincaFromParcela = (parcelaId: string): string => {
                const found = this.form.lists.parcelas.filtered.find(
                    (item) => String((item.value as ParcelasModel)?.id ?? '') === parcelaId
                );
                return String((found?.value as ParcelasModel)?.id_finca ?? '');
            };

            const ubicacionOrigen  = extractId(this.form.model.ubicacion);
            const ubicacionDestino = extractId(this.form.model.ubicacion_destino);

            if (ubicacionOrigen !== '' && ubicacionDestino !== '') {
                const idFincaOrigen = extractIdFincaFromParcela(ubicacionOrigen);

                const rawValues = this.formBuilderRef?.model?.getRawValue() as Record<string, unknown> ?? {};
                const idCliente = extractId(rawValues['id_cliente']) || String(this.form.cliente?.id ?? '');
                const idUsuario = extractId(rawValues['id_usuario']) || String(this.form.usuario?.id ?? '');

                const { id: _omit, ...modelSinId } = this.form.model;

                const mirrorBase = {
                    ...modelSinId,
                    tipo: 'S',
                    ubicacion: ubicacionDestino,
                    ubicacion_destino: ubicacionOrigen,
                    id_finca: idFincaOrigen,
                    id_cliente: idCliente,
                    id_usuario: idUsuario,
                    id_comercio: extractId(this.form.model['id_comercio']),
                };
                const mirrorPayload = ReactiveFormPayloadSerializer.serialize(this.form.formFields, mirrorBase);

                const params = new HttpParams()
                    .append('p1', 'generic')
                    .append('p2', 'stock_new');

                firstValueFrom(
                    this.http.post<any>(`${environment.serverUrl}ws/index.php`, mirrorPayload, { params })
                ).then(() => {
                    console.log('espejo salida creado');
                }).catch((e) => {
                    console.error('error creando espejo:', e);
                });
            }
        }

        if (this.form.env.appName === 'viverosmuzale') {
            this.syncPlantasStock(extractId);
            this.syncCultivoParcelaFromStock(extractId);
        }
    }

    private syncPlantasStock(extractId: (val: unknown) => string): void {
        if (this.getType() !== FormType.CREATION) {
            return;
        }

        const tipo = String(this.form.model.tipo ?? '');

        if (tipo !== 'E' && tipo !== 'S') {
            return;
        }

        if (String(this.form.model.tipo_dato ?? '') !== 'Planta') {
            return;
        }

        const rawValues = this.formBuilderRef?.model?.getRawValue() as Record<string, unknown> ?? {};
        const ubicacionId = extractId(rawValues['ubicacion']);

        if (ubicacionId === '') {
            return;
        }

        const udsProducidas = Number(String(this.form.model['uds_producidas'] ?? '0').replace(',', '.'));

        if (udsProducidas === 0) {
            return;
        }

        const parcelaEncontrada = this.form.lists.parcelas.filtered.find(
            (item) => String((item.value as ParcelasModel)?.id ?? '') === ubicacionId
        );

        if (!parcelaEncontrada) {
            return;
        }

        const parcela = parcelaEncontrada.value as ParcelasModel;
        const plantasActuales = Number(String(parcela.plantas ?? '0').replace(',', '.'));
        const nuevasPlantas = tipo === 'E'
            ? plantasActuales + udsProducidas
            : plantasActuales - udsProducidas;

        this.fincasApi.parcelasPUT.safePerform({ id: ubicacionId, plantas: String(nuevasPlantas) } as any);

        this.fincasApi.parcelasPUT.response(() => {
            if (!this.fincasApi.parcelasPUT.hasError) {
                parcela.plantas = String(nuevasPlantas);
            } else {
                console.error('error actualizando plantas');
            }
        });
    }

    private syncCultivoParcelaFromStock(extractId: (val: unknown) => string): void {
        if (this.getType() !== FormType.CREATION) {
            return;
        }

        if (String(this.form.model.tipo ?? '') !== 'P') {
            return;
        }

        if (String(this.form.model.tipo_dato ?? '') !== 'Planta') {
            return;
        }

        const rawValues = this.formBuilderRef?.model?.getRawValue() as Record<string, unknown> ?? {};
        const ubicacionId = extractId(rawValues['ubicacion']);

        if (ubicacionId === '') {
            return;
        }

        const idProducto = String(this.form.model.ids_productos ?? '').trim();
        const idVariedad = String(this.form.model.id_variedad ?? '').trim();

        if (idProducto === '') {
            return;
        }

        const cultivoEncontrado = this.form.lists.cultivosAll.filtered.find(
            (item) => String((item.value as any)?.id ?? '') === idProducto
        );

        const codigoCultivo = String((cultivoEncontrado?.value as any)?.codigo_cultivo ?? '').trim();

        if (codigoCultivo === '') {
            return;
        }

        const parcelaEncontrada = this.form.lists.parcelas.filtered.find(
            (item) => String((item.value as ParcelasModel)?.id ?? '') === ubicacionId
        );

        if (!parcelaEncontrada) {
            return;
        }

        const parcela = parcelaEncontrada.value as ParcelasModel;

        this.fincasApi.parcelasPUT.safePerform({
            id: ubicacionId,
            id_cultivo: codigoCultivo,
            id_variedad: idVariedad
        } as any);

        this.fincasApi.parcelasPUT.response(() => {
            if (!this.fincasApi.parcelasPUT.hasError) {
                parcela.id_cultivo = codigoCultivo;
                parcela.id_variedad = idVariedad;
            } else {
                console.error('error actualizando cultivo/variedad de parcela');
            }
        });
    }

    /**
     * Loads all dropdown sources required by the form.
     */
    private getFieldsBuildRequests(): Array<(resolve: (resolved: boolean) => void) => void> {
        let productoParams: Record<string, string> = {};

        if (this.form.env.appName === 'vginer') {
            productoParams = { tipo: 'fertilizante' };
        }

        const builds: Array<(resolve: (resolved: boolean) => void) => void> = [
            build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre')
        ];

        if (this.form.env.appName === 'covidai' || this.form.env.appName === 'sendra' || this.form.env.appName === 'viverosmuzale') {
            builds.push(
                build(this.fincasApi.sectoresGET, this.form.lists.sectores, 'nombre', true)
            );
        }

        if (['covidai', 'bravosol', 'sendra', 'viverosmuzale'].includes(this.form.env.appName)) {
            builds.push(
                build(this.fincasApi.parcelasGET, [this.form.lists.parcelas, this.form.lists.parcelasDestino],
                    (p: ParcelasModel) => `${p.finca ?? ''} - ${p.nombre ?? ''}`, true)
            );
        }

        if (!this.form.env.showStockTipoDeDato) {
            if (this.form.env.appName === 'covidai') {
                builds.push(
                    buildParams(
                        this.siexApi.portainjertosGET,
                        productoParams,
                        [this.form.lists.productos, this.form.lists.articulos],
                        'familia'
                    )
                );
            } else {
                builds.push(
                    buildParams(
                        this.productosApi.productos.GET,
                        productoParams,
                        this.form.lists.productos,
                        'nombre'
                    )
                );
            }
        }

        if (this.form.env.showStockTipoDeDato) {
            if (this.form.env.appName === 'covidai') {
                builds.push(
                    build(this.productosApi.cultivosPatron.GET, this.form.lists.productosAll, 'nombre'),
                    build(this.siexApi.portainjertosGET, [this.form.lists.cultivosAll, this.form.lists.auxCultivos], 'descripcion')
                );
            } else {
                builds.push(
                    build(this.productosApi.productos.GET, this.form.lists.productosAll, 'nombre'),
                    build(
                        this.form.env.hasPlantas ? this.formApi.variedadesGET : this.siexApi.vistaCultivoVariedadGET,
                        [this.form.lists.cultivosAll, this.form.lists.auxCultivos],
                        'cultivo'
                    )
                );
            }

            builds.push(
                build(this.ventasApi.compradoresGET, this.form.lists.comercios, 'razon_social', true),
                build(this.clientesApi.clientesGET, this.form.lists.clientes, 'nombre')
            );
        }

        if (this.form.env.appName === 'bravosol') {
            builds.push(
                buildParams(
                    this.siexApi.portainjertosGET,
                    productoParams,
                    this.form.lists.articulos,
                    'familia'
                )
            );
        }

        return builds;
    }

    /**
     * Loads remote data and prepares local dropdown state.
     */
    private initFormData(): Promise<void> {
        this.loadingData = true;

        return pool(this.getFieldsBuildRequests(), () => {
            this.loadingData = false;
        }).then(() => {
            this.postProcessLists();
            this.applyInitialState();
            this.form.refreshProductosList();
            this.form.refreshLineFieldLabels();
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
        }).catch((error: unknown) => {
            console.error('Error loading entradas form data:', error);
            this.loadingData = false;
            this.finalizeExternalDropdowns();
        });
    }

    /**
     * Applies local dataset transformations once remote data is available.
     */
    private postProcessLists(): void {
        if (this.form.env.appName === 'pyf') {
            this.form.lists.fincas.filtered = [...this.form.lists.fincas.filtered].reverse();
            this.form.lists.fincas.values = [...this.form.lists.fincas.filtered];
        }

        if (this.form.lists.cultivosAll.filtered.length > 0) {
            this.form.lists.cultivosAll.filtered = this.form.lists.cultivosAll.filtered.filter((item, index, array) => {
                if (index === 0) {
                    return true;
                }

                return array[index - 1]?.label !== item.label;
            });

            this.form.lists.cultivosAll.values = [...this.form.lists.cultivosAll.filtered];
        }

        this.form.refreshProductosList();
        this.form.refreshLineFieldLabels();
    }

    /**
     * Applies startup defaults after dropdowns are loaded.
     */
    private applyInitialState(): void {
        if (this.getType() === FormType.CREATION && this.form.lists.fincas.filtered.length === 2) {
            const finca = this.form.lists.fincas.filtered[1]?.value as FincasModel | null;

            this.form.model.id_finca = String(finca?.id ?? '');
        }

        if (this.form.env.appName === 'ava' && !this.form.model.tipo) {
            this.form.model.tipo = 'E';
        }

        if (this.form.env.forcesPlantType && !this.form.model.tipo_dato) {
            this.form.model.tipo_dato = 'Semilla';
        }

        this.syncProveedorFromComercio();

        if (this.form.env.appName === 'covidai' && this.getType() !== FormType.CREATION) {
            this.syncSelectedParcel();
        }
    }

    /**
     * Builds a local legacy view from repeatable FormArray values without syncing hidden controls.
     */
    private withRepeatableLegacyValues(formModel: ReactiveFormValue<StockageModel>): ReactiveFormValue<StockageModel> {
        const legacyValues = ReactiveFormPayloadSerializer.serializeRepeatableLegacyFields(
            this.form.formFields,
            formModel as Record<string, unknown>
        );

        return { ...formModel, ...legacyValues } as ReactiveFormValue<StockageModel>;
    }

    /**
     * Refreshes builder mappings after local option lists or labels are replaced.
     */
    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.refreshExternalDropdownMappings();
    }

    /**
     * Syncs the provider text field with the selected company.
     */
    private syncProveedorFromComercio(): void {
        const selected = this.resolveSelectedDropdownEntity<Compradores>('id_comercio', this.form.lists.comercios);

        if (selected?.razon_social != null) {
            this.form.model.proveedor = selected.razon_social;
        }
    }

    /**
     * Clears serialized line payload fields when the product type changes.
     */
    private clearLinePayload(): void {
        this.form.model.ids_productos = '';
        this.form.model.ids_tipos = '';
        this.form.model.array_unidades = '';
        this.form.model.array_lotes = '';
        this.form.model.array_marcas = '';
        this.form.model.array_envases = '';
        this.form.model.array_lotes_conselleria = '';
        this.form.model.unidades_medida = '';
        this.form.model.id_variedad = '';
        this.form.model.tipo_semilla = '';

        const lineasGroup = this.formBuilderRef?.model?.get('lineas_group');

        if (!(lineasGroup instanceof FormArray)) {
            return;
        }

        lineasGroup.controls.forEach((rowControl) => rowControl.reset({}, { emitEvent: false }));
        lineasGroup.updateValueAndValidity({ emitEvent: true });
    }

    /**
     * Keeps parcel controls aligned in Covidai edition flows without mutating DropdownData.selected.
     */
    private syncSelectedParcel(): void {
        const ubicacion = String(this.form.model.ubicacion ?? '');

        if (ubicacion === '') {
            return;
        }

        const selected = this.form.lists.parcelas.filtered.find((parcelaItem) => {
            const parcela = parcelaItem.value as ParcelasModel | null;

            return String(parcela?.id ?? '') === ubicacion;
        });
        const selectedId = String((selected?.value as ParcelasModel | null)?.id ?? ubicacion);

        this.form.model.ubicacion = selectedId;
        this.formBuilderRef?.model?.get('ubicacion')?.setValue(selectedId, { emitEvent: false });
        this.formBuilderRef?.model?.get('id_parcela')?.setValue(selectedId, { emitEvent: false });
    }

    private resolveSelectedDropdownEntity<T extends object>(fieldName: string, dropdown: DropdownData<T>): T | null {
        const currentValue = this.formBuilderRef?.model?.get(fieldName)?.value
            ?? (this.form.model as Record<string, unknown>)[fieldName];

        if (this.isRecord(currentValue)) {
            return currentValue as T;
        }

        const tokens = this.getReactiveValueTokens(currentValue);

        if (tokens.length === 0) {
            return null;
        }

        const options = [...dropdown.values, ...dropdown.filtered];

        for (const option of options) {
            const entity = this.getDropdownOptionEntity<T>(option);

            if (entity === null) {
                continue;
            }

            if (this.dropdownOptionMatchesValue(option, entity, tokens)) {
                return entity;
            }
        }

        return null;
    }

    private getReactiveValueTokens(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.flatMap((item) => this.getReactiveValueTokens(item));
        }

        if (this.isRecord(value)) {
            return ['id', 'codigo', 'value']
                .map((key) => String(value[key] ?? '').trim())
                .filter((token) => token.length > 0);
        }

        return String(value ?? '')
            .split(';')
            .map((token) => token.trim())
            .filter((token) => token.length > 0);
    }

    private dropdownOptionMatchesValue<T extends object>(
        option: { readonly value?: unknown; readonly raw?: unknown; readonly label?: string },
        entity: T,
        tokens: string[]
    ): boolean {
        const optionTokens = [
            option.value,
            option.raw,
            option.label,
            this.asRecord(entity)['id'],
            this.asRecord(entity)['codigo'],
            this.asRecord(entity)['referencia'],
            this.asRecord(entity)['razon_social'],
            this.asRecord(entity)['nombre']
        ]
            .flatMap((value) => this.getReactiveValueTokens(value))
            .filter((token, index, values) => values.indexOf(token) === index);

        return tokens.some((token) => optionTokens.includes(token));
    }

    private getDropdownOptionEntity<T extends object>(
        option: { readonly value?: unknown; readonly raw?: unknown }
    ): T | null {
        if (this.isRecord(option.raw)) {
            return option.raw as T;
        }

        if (this.isRecord(option.value)) {
            return option.value as T;
        }

        return null;
    }

    private asRecord(value: object): Record<string, unknown> {
        return value as Record<string, unknown>;
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }



    private syncFincaFromUbicacionDestino(): void {
        const extractId = (val: unknown): string => {
            if (val == null) return '';
            if (typeof val === 'object') return String((val as Record<string, unknown>)['id'] ?? '');
            return String(val);
        };

        const ubicacionDestinoId = extractId(this.form.model.ubicacion_destino);

        if (ubicacionDestinoId === '') {
            return;
        }

        const found = this.form.lists.parcelas.filtered.find(
            (item) => String((item.value as ParcelasModel)?.id ?? '') === ubicacionDestinoId
        );

        const idFincaDestino = String((found?.value as ParcelasModel)?.id_finca ?? '');

        if (idFincaDestino !== '') {
            this.form.model.id_finca = idFincaDestino;

            const control = this.formBuilderRef?.model?.get('id_finca');
            if (control) {
                control.setValue(idFincaDestino, { emitEvent: false });
            }
        }
    }
}
